import { AfterViewInit, Component, Inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { passwordValidator } from '../../Validators/checkPassword.validator';
import { matchPasswordValidator } from '../../Validators/checkBothPasswords.validator';
import { ConfirmEmailService } from '../confirm-email/confirm-email.service';
import { Router } from '@angular/router';
import { isPlatformBrowser } from '@angular/common';

declare var gsap: any;

@Component({
    selector: 'app-create-password',
    templateUrl: './create-password.component.html',
    styleUrl: './create-password.component.scss',
    standalone: false
})
export class CreatePasswordComponent implements OnInit, AfterViewInit {
  passwordForm: FormGroup;
  showSuccessToast = false;
  capitalLetterFlag = false;
  uniqueCharacterFlag = false;
  stringLengthGt12Flag = false;
  stringNumFlag = false;
  email = ''
  showErrorToast = false;
  error = ''

  // ==================== PASSWORD VISIBILITY PROPERTIES - START ====================
  isPasswordHidden = true;
  isConfirmPasswordHidden: boolean = true;
  // ==================== PASSWORD VISIBILITY PROPERTIES - END ====================

  // Flag to control whether eye animations should track mouse movement
  isEyeTrackingEnabled = true;

  isPasswordValid = signal({
    hasSpecialCharacter: false,
    hasCapitalLetter: false,
    isLongEnough: false,
    hasNumber: false
  });
  successMsg: string;

  constructor(private fb: FormBuilder, private confirmEmail: ConfirmEmailService, private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }
  ngOnInit(): void {
    this.passwordForm = this.fb.group({
      password: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: matchPasswordValidator('password', 'confirmPassword') })
    this.confirmEmail.email$.subscribe((res: any) => {
      this.email = res;
    })
  }
  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeEyeAnimations();
      this.passwordForm.get('password')?.valueChanges.subscribe((password) => {
        this.updatePasswordValidation(password);
      });
      this.passwordForm.get('confirmPassword')?.valueChanges.subscribe((password) => {
        this.updatePasswordValidation(password);
      });
    }
  }



  // ==================== EYE ANIMATIONS AND INTERACTIONS - START ====================
  private initializeEyeAnimations(): void {
    const eyes = [
      '#shape01_eye',
      '#shape02_left_eye', '#shape02_right_eye',
      '#shape03_left_eye', '#shape03_right_eye',
      '#shape04_left_eye', '#shape04_right_eye',
      '#shape05_left_eye', '#shape05_right_eye',
      '#shape06_left_eye', '#shape06_right_eye',
      '#shape07_left_eye', '#shape07_right_eye'
    ];

    // Group eyes by shape for synchronized blinking
    const eyeGroups = [
      ['#shape01_eye'],
      ['#shape02_left_eye', '#shape02_right_eye'],
      ['#shape03_left_eye', '#shape03_right_eye'],
      ['#shape04_left_eye', '#shape04_right_eye'],
      ['#shape05_left_eye', '#shape05_right_eye'],
      ['#shape06_left_eye', '#shape06_right_eye'],
      ['#shape07_left_eye', '#shape07_right_eye']
    ];

    // Track which shapes are currently blinking
    const blinkingShapes = new Set();

    // Blinking animation function
    const blinkShape = (eyeGroup: string[]) => {
      if (blinkingShapes.has(eyeGroup)) return; // Prevent overlapping blinks

      blinkingShapes.add(eyeGroup);

      eyeGroup.forEach(eyeId => {
        const eye = document.querySelector(eyeId) as SVGCircleElement;
        if (eye) {
          // Store original radius if not already stored
          if (!eye.hasAttribute('data-original-r')) {
            eye.setAttribute('data-original-r', eye.getAttribute('r') || '16');
          }

          const originalRadius = parseFloat(eye.getAttribute('data-original-r') || '16');

          // Blink animation: shrink to 0, then back to original
          gsap.timeline()
            .to(eye, {
              duration: 0.08,
              ease: "power2.inOut",
              attr: { r: 0 }
            })
            .to(eye, {
              duration: 0.08,
              ease: "power2.inOut",
              attr: { r: originalRadius },
              onComplete: () => {
                blinkingShapes.delete(eyeGroup);
              }
            });
        }
      });
    };

    // Start random blinking for all shapes
    const startBlinking = () => {
      eyeGroups.forEach((eyeGroup, index) => {
        const scheduleNextBlink = () => {
          // Random interval between 2-6 seconds
          // NOSONAR: Math.random() is safe here - used only for UI animation timing
          const randomDelay = Math.floor(Math.random() * 4000) + 2000;

          setTimeout(() => {
            // Check if any other shape is currently blinking
            if (blinkingShapes.size === 0) {
              blinkShape(eyeGroup);
            }
            scheduleNextBlink(); // Schedule the next blink
          }, randomDelay);
        };

        // Start with random initial delay to stagger the first blinks
        setTimeout(() => {
          scheduleNextBlink();
        }, Math.floor(Math.random() * 3000)); // NOSONAR: Safe for UI animation timing
      });
    };

    // Initialize blinking
    startBlinking();

    // Mouse follow animation
    document.addEventListener('mousemove', (e) => {
      // Only track mouse if eye tracking is enabled (not paused for privacy)
      if (!this.isEyeTrackingEnabled) return;

      eyes.forEach(eyeId => {
        const eye = document.querySelector(eyeId) as SVGCircleElement;
        if (eye) {
          // Get the SVG container to calculate relative positions
          const svg = document.querySelector('#eyesAnimation') as SVGSVGElement;
          if (svg) {
            // Get SVG bounding box and mouse position relative to SVG
            const svgRect = svg.getBoundingClientRect();
            const svgMouseX = e.clientX - svgRect.left;
            const svgMouseY = e.clientY - svgRect.top;

            // Scale mouse coordinates to SVG viewBox (800x800)
            const scaleX = 800 / svgRect.width;
            const scaleY = 800 / svgRect.height;
            const mouseX = svgMouseX * scaleX;
            const mouseY = svgMouseY * scaleY;

            // Get original eye position (store if not already stored)
            if (!eye.hasAttribute('data-original-cx')) {
              eye.setAttribute('data-original-cx', eye.getAttribute('cx') || '0');
              eye.setAttribute('data-original-cy', eye.getAttribute('cy') || '0');
            }

            const originalCx = parseFloat(eye.getAttribute('data-original-cx') || '0');
            const originalCy = parseFloat(eye.getAttribute('data-original-cy') || '0');
            const eyeRadius = parseFloat(eye.getAttribute('r') || '16');

            // Calculate direction from original eye position to mouse
            const deltaX = mouseX - originalCx;
            const deltaY = mouseY - originalCy;
            const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            // Maximum movement range for the pupil (should be less than eye socket radius)
            // Assuming eye socket is roughly 3x the pupil size, so max movement is about 1/3 of that
            const maxMove = Math.max(eyeRadius * 0.8, 8); // At least 8 pixels movement

            // Calculate new position (limit movement within the eye socket)
            let moveX = 0;
            let moveY = 0;

            if (distance > 0) {
              const normalizedX = deltaX / distance;
              const normalizedY = deltaY / distance;

              // Limit movement to maxMove radius from original position
              const actualMove = Math.min(maxMove, distance * 0.05);
              moveX = normalizedX * actualMove;
              moveY = normalizedY * actualMove;
            }

            // Calculate final position relative to original position
            const newCx = originalCx + moveX;
            const newCy = originalCy + moveY;

            // Console log for live eye position tracking
            // console.log(`Eye: ${eyeId} | Position: (${newCx.toFixed(2)}, ${newCy.toFixed(2)}) | Original: (${originalCx}, ${originalCy}) | Move: (${moveX.toFixed(2)}, ${moveY.toFixed(2)})`);

            // Animate the eye position using GSAP
            gsap.to(eye, {
              duration: 0.3,
              ease: "power2.out",
              attr: {
                cx: newCx,
                cy: newCy
              }
            });
          }
        }
      });
    });
  }

  // Method to position all eyes to look away when password is visible
  lookAwayForPrivacy(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    // Disable mouse tracking to pause eye animations
    this.isEyeTrackingEnabled = false;

    // All eye selectors with their exact positions from the console log
    const eyePositions: { [key: string]: { x: number; y: number } } = {
      '#shape01_eye': { x: 404.52, y: 174.14 },
      '#shape02_left_eye': { x: 417.05, y: 387.55 },
      '#shape02_right_eye': { x: 471.12, y: 398.27 },
      '#shape03_left_eye': { x: 606.42, y: 638.07 },
      '#shape03_right_eye': { x: 670.55, y: 629.40 },
      '#shape04_left_eye': { x: 229.58, y: 391.45 },
      '#shape04_right_eye': { x: 276.97, y: 375.61 },
      '#shape05_left_eye': { x: 74.00, y: 492.77 },
      '#shape05_right_eye': { x: 129.24, y: 493.28 },
      '#shape06_left_eye': { x: 172.77, y: 169.59 },
      '#shape06_right_eye': { x: 219.96, y: 190.29 },
      '#shape07_left_eye': { x: 573.16, y: 186.28 },
      '#shape07_right_eye': { x: 624.09, y: 174.85 }
    };

    // Set each eye to its specific position
    Object.entries(eyePositions).forEach(([eyeSelector, position]) => {
      const eye = document.querySelector(eyeSelector) as SVGCircleElement;
      if (eye) {
        // Animate the eye to the exact position
        gsap.to(eye, {
          duration: 0.4,
          ease: "power2.out",
          attr: {
            cx: position.x,
            cy: position.y
          }
        });
      }
    });
  }

  // Method to resume normal mouse tracking
  resumeMouseTracking(): void {
    // Re-enable mouse tracking
    this.isEyeTrackingEnabled = true;

    // Simply trigger the existing mouse tracking logic by simulating a mouse move
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;

    // Create a fake mouse event to reset eyes to center
    const fakeMoveEvent = new MouseEvent('mousemove', {
      clientX: centerX,
      clientY: centerY
    });

    // Dispatch the event to restart normal tracking
    document.dispatchEvent(fakeMoveEvent);
  }
  // ==================== EYE ANIMATIONS AND INTERACTIONS - END ====================

  submitForm() {
    let payload = {
      "emailId": this.email,
      "password": this.passwordForm.get('password').value
    }
    this.confirmEmail.createPassword(payload).subscribe((res: any) => {
      if (res.code == 200) {
        this.showSuccessToast = true;
        this.successMsg = 'Your password has been successfully created. Please log in again.';
        setTimeout(() => {
          this.router.navigateByUrl('/login')
          this.showSuccessToast = false;
        }, 3000);
      } else if (res.code == 500) {
        this.error = res.result.error;
        this.showErrorToast = true;
        setTimeout(() => {
          this.showErrorToast = false;
          this.error = ''
        }, 5000)
      }
    })
  }
  checkPasswordRules() {
    this.uniqueCharacterFlag = this.containsSpecialCharacter(this.passwordForm.get('password').value)
    this.capitalLetterFlag = this.containsCapitalLetter(this.passwordForm.get('password').value)
    this.stringLengthGt12Flag = this.passwordForm.get('password').value.trim().length >= 8
    this.stringNumFlag = this.containsLowercaseAndNumber(this.passwordForm.get('password').value)
  }
  containsCapitalLetter(input: string): boolean {
    const capitalLetterRegex = /[A-Z]/;
    return capitalLetterRegex.test(input);
  }
  containsLowercaseAndNumber(input: string): boolean {
    const lowercasePattern = /[a-z]/;
    const numberPattern = /[0-9]/;
    return lowercasePattern.test(input) && numberPattern.test(input);
  }
  containsSpecialCharacter(input: string): boolean {
    const specialCharacterRegex = /[!@#$%^&*()_+=\[\]{};':"\\|,.<>\/?]/;
    // const specialCharacterRegex = /[!@#$%^&*(),.?":{}|<>]/;
    return specialCharacterRegex.test(input);
  }

  // ==================== PASSWORD VALIDATION METHODS - START ====================
  private updatePasswordValidation(password: string): void {
    this.isPasswordValid.set({
      hasSpecialCharacter: /[!@#$%^&*()_+=\[\]{};':"\\|,.<>\/?]/.test(password),
      hasCapitalLetter: /[A-Z]/.test(password) && /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      isLongEnough: password.length >= 8,
    });
  }

  // ==================== PASSWORD VISIBILITY METHODS - START ====================
  showHidePass(): void {
    this.isPasswordHidden = !this.isPasswordHidden;

    // If any password field is visible (not hidden), make eyes look away for privacy
    if (!this.isPasswordHidden || !this.isConfirmPasswordHidden) {
      this.lookAwayForPrivacy();
    } else {
      // Both password fields are now hidden, resume normal mouse tracking
      this.resumeMouseTracking();
    }
  }

  showHideConfirmPass(): void {
    this.isConfirmPasswordHidden = !this.isConfirmPasswordHidden;

    // If any password field is visible (not hidden), make eyes look away for privacy
    if (!this.isPasswordHidden || !this.isConfirmPasswordHidden) {
      this.lookAwayForPrivacy();
    } else {
      // Both password fields are now hidden, resume normal mouse tracking
      this.resumeMouseTracking();
    }
  }
  // ==================== PASSWORD VISIBILITY METHODS - END ====================

}
